import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MessageCircle, X, Maximize2, Minimize2, Minus } from 'lucide-react';
import ChatContent from './ChatContent';
import { getServiceLineGross, formatOfferTotalText } from '../utils/offerLineDisplay';

// Darken a hex color by a factor 0–1 (e.g. 0.15 = 15% darker)
function darkenHex(hex, factor = 0.15) {
  const match = hex.replace('#', '').match(hex.length <= 4 ? /^(.)(.)(.)$/ : /^(.{2})(.{2})(.{2})$/);
  if (!match) return hex;
  const toNum = (s) => (s.length === 1 ? parseInt(s + s, 16) : parseInt(s, 16));
  const r = Math.max(0, Math.round(toNum(match[1]) * (1 - factor)));
  const g = Math.max(0, Math.round(toNum(match[2]) * (1 - factor)));
  const b = Math.max(0, Math.round(toNum(match[3]) * (1 - factor)));
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

const SM_BREAKPOINT = 640;
const EMBED_EDGE_PADDING = 16;

function computeEmbedDimensions(isOpen, isMaximized, isMinimized, showSpeechBubble, hostWidth, hostHeight) {
  const isMobile = hostWidth < SM_BREAKPOINT;
  const pad = EMBED_EDGE_PADDING;

  if (!isOpen) {
    const btnSize = 64;
    let width = btnSize + pad;
    let height = btnSize + pad;
    if (showSpeechBubble) {
      width = Math.max(width, 268);
      // Floor only. The closed launcher is measured before the resize post;
      // long hints (the German default wraps to ~6 lines) exceed 64+16+100.
      height += 100;
    }
    return { width, height, isOpen: false, mobileFullscreen: false };
  }

  if (isMobile) {
    return {
      width: hostWidth,
      height: hostHeight,
      isOpen: true,
      mobileFullscreen: true,
    };
  }

  let w = 384;
  let h = 500;
  if (isMaximized) {
    w = 600;
    h = 700;
  } else if (isMinimized) {
    w = 320;
    h = 400;
  }
  return {
    width: w + pad,
    height: h + pad,
    isOpen: true,
    mobileFullscreen: false,
  };
}

// Launcher border box plus the shadow gutter. The root is h-full, so measuring
// that box echoes the iframe and would keep a too-short frame.
function resolveClosedEmbedHeight(formulaHeight, launcherEl) {
  if (!launcherEl) return formulaHeight;
  const measured = launcherEl.getBoundingClientRect().height;
  if (measured <= 0) return formulaHeight;
  return Math.max(formulaHeight, Math.ceil(measured) + EMBED_EDGE_PADDING);
}

const EmbeddedChatbot = () => {
  const { t } = useTranslation();
  const { chatbotId } = useParams();
  const [searchParams] = useSearchParams();
  const isIframeEmbed =
    window.parent !== window && searchParams.get('embed') === '1';
  const widgetRootRef = useRef(null);
  const launcherRef = useRef(null);
  const embedFrameRef = useRef(null);
  const [chatbot, setChatbot] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [conversationEnded, setConversationEnded] = useState(false);
  const [hasDocument, setHasDocument] = useState(false);
  const [speechBubbleDismissed, setSpeechBubbleDismissed] = useState(false);
  const [configFailed, setConfigFailed] = useState(false);
  const [hostViewport, setHostViewport] = useState({ width: 1024, height: 768 });

  // Transparent page background when embedded (e.g. in iframe) so only the widget is visible
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prevHtmlBg = html.style.backgroundColor;
    const prevBodyBg = body.style.backgroundColor;
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;
    const prevBodyMargin = body.style.margin;
    html.style.backgroundColor = 'transparent';
    body.style.backgroundColor = 'transparent';
    if (isIframeEmbed) {
      html.style.overflow = 'hidden';
      body.style.overflow = 'hidden';
      body.style.margin = '0';
    }
    return () => {
      html.style.backgroundColor = prevHtmlBg;
      body.style.backgroundColor = prevBodyBg;
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
      body.style.margin = prevBodyMargin;
    };
  }, [isIframeEmbed]);

  useEffect(() => {
    // Load chatbot configuration
    fetchChatbotConfig();
  }, [chatbotId]);

  useEffect(() => {
    if (!isIframeEmbed) return undefined;
    window.parent.postMessage({ type: 'chatbot:request-host-size' }, '*');
    const onHostMessage = (event) => {
      if (event.data?.type !== 'chatbot:host-size') return;
      setHostViewport({
        width: event.data.width,
        height: event.data.height,
      });
    };
    window.addEventListener('message', onHostMessage);
    return () => window.removeEventListener('message', onHostMessage);
  }, [isIframeEmbed]);

  const postEmbedResize = useCallback(
    (width, height, open, mobileFullscreen = false, ready = false) => {
      if (!isIframeEmbed || !chatbotId) return;
      window.parent.postMessage(
        {
          type: 'chatbot:resize',
          chatbotId,
          width: Math.ceil(width),
          height: Math.ceil(height),
          isOpen: open,
          mobileFullscreen: !!mobileFullscreen,
          ready: !!ready,
        },
        '*'
      );
    },
    [isIframeEmbed, chatbotId]
  );

  const showSpeechBubble =
    isIframeEmbed && !isOpen && !speechBubbleDismissed && !!chatbot;

  embedFrameRef.current = {
    isOpen,
    isMaximized,
    isMinimized,
    showSpeechBubble,
    configFailed,
    hostViewport,
    chatbot,
  };

  const publishEmbedFrame = useCallback(() => {
    if (!isIframeEmbed) return;
    const frame = embedFrameRef.current;
    if (!frame?.chatbot && !frame?.configFailed) return;

    const dims = computeEmbedDimensions(
      frame.isOpen,
      frame.isMaximized,
      frame.isMinimized,
      frame.showSpeechBubble || frame.configFailed,
      frame.hostViewport.width,
      frame.hostViewport.height
    );
    if (!frame.isOpen) {
      dims.height = resolveClosedEmbedHeight(dims.height, launcherRef.current);
    }
    postEmbedResize(dims.width, dims.height, dims.isOpen, dims.mobileFullscreen, true);
  }, [isIframeEmbed, postEmbedResize]);

  useLayoutEffect(() => {
    if (!isIframeEmbed) return;
    // The host iframe stays hidden until this message. Wait until config has
    // loaded so the first visible frame already fits the button and the bubble.
    if (!chatbot && !configFailed) return;
    publishEmbedFrame();
  }, [
    isIframeEmbed,
    isOpen,
    isMaximized,
    isMinimized,
    showSpeechBubble,
    hostViewport,
    chatbot,
    configFailed,
    publishEmbedFrame,
  ]);

  useLayoutEffect(() => {
    if (!isIframeEmbed || isOpen || !chatbot) return undefined;
    const launcher = launcherRef.current;
    if (!launcher || typeof ResizeObserver === 'undefined') return undefined;

    // Hint copy can change after the first post (locale or a custom hint)
    // without a state field the resize effect depends on.
    const observer = new ResizeObserver(() => {
      publishEmbedFrame();
    });
    observer.observe(launcher);
    return () => observer.disconnect();
  }, [isIframeEmbed, isOpen, chatbot, publishEmbedFrame]);

  const fetchChatbotConfig = async () => {
    try {
      // Use the public embed config endpoint (no authentication required)
      // Add cache-busting to ensure we get the latest version
      const response = await fetch(`/api/embed/${chatbotId}/config?t=${Date.now()}`);
      if (response.ok) {
        const data = await response.json();
        setChatbot(data);
        
        // Check if chatbot has services (price list)
        const hasServices = data.services && Array.isArray(data.services) && data.services.length > 0;
        setHasDocument(hasServices);
        
        // First message: configurable greeting, or fallback to locale strings
        let greetingMessage;
        if (!hasServices) {
          greetingMessage = data.companyName
            ? t('embed.noDocWithCompany', { company: data.companyName })
            : t('embed.noDocNoCompany');
        } else if (data.greeting && data.greeting.trim()) {
          const company = data.companyName || '';
          greetingMessage = data.greeting.trim().replace(/\{\{companyName\}\}/g, company).replace(/\{\{BETRIEBNAME\}\}/g, company);
        } else {
          greetingMessage = data.companyName
            ? t('embed.greetingWithCompany', { company: data.companyName })
            : t('embed.greetingNoCompany');
        }
        
        setMessages([
          {
            role: 'assistant',
            content: greetingMessage
          }
        ]);
      } else {
        throw new Error('Chatbot not found');
      }
    } catch (error) {
      console.error('Error loading chatbot:', error);
      setMessages([
        {
          role: 'assistant',
          content: t('embed.loadError')
        }
      ]);
      setHasDocument(false);
      setConfigFailed(true);
    }
  };

  // Function to extract offer details from structured response
  const extractOfferDetails = (structuredResponse) => {
    if (!structuredResponse || !structuredResponse.offer) {
      return null;
    }

    const offer = structuredResponse.offer;
    
    // Format offer details for display
    let offerDetails = structuredResponse.content || '';
    
    // Add service details if available
    if (offer.services && offer.services.length > 0) {
      offerDetails += '\n\nServices included:\n';
      offer.services.forEach((service, index) => {
        offerDetails += `${index + 1}. ${service.name}`;
        if (service.description) {
          offerDetails += ` - ${service.description}`;
        }
        if (service.price !== undefined) {
          const lineGross = getServiceLineGross(service);
          offerDetails += ` (${offer.currency || 'EUR'} ${lineGross.toFixed(2)} brutto)`;
        }
        offerDetails += '\n';
      });
    }
    
    const totalAmount = formatOfferTotalText(offer) || 'TBD';

    return {
      offerDetails,
      totalAmount,
      services: offer.services || [],
      currency: offer.currency || 'EUR',
      validUntil: offer.validUntil || null,
      notes: offer.notes || '',
      unlistedItems: Array.isArray(offer.unlistedItems) ? offer.unlistedItems : [],
      delivery: offer.delivery === true,
      setup: offer.setup === true,
      servicePersonnel: offer.servicePersonnel === true,
      otherService: offer.otherService === true,
      sharedSelections: Array.isArray(offer.sharedSelections) ? offer.sharedSelections : [],
      displayMode: offer.displayMode,
      priceRange: offer.priceRange,
      createdAt: new Date()
    };
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;
    
    // Safeguard: Check if document is uploaded before starting conversation
    if (!hasDocument) {
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: t('embed.noDocCannotStart') 
      }]);
      return;
    }

    const userMessage = inputMessage.trim();
    setInputMessage('');
    
    // Add user message to chat
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const response = await fetch(`/api/embed/${chatbotId}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: userMessage,
          conversationId
        }),
      });

      const data = await response.json();
      
      if (response.ok) {
        // Update conversationId from response (important for first message)
        const currentConversationId = data.conversationId;
        setConversationId(currentConversationId);
        
        // Handle structured response (flattened format) or fallback to plain text (backward compatibility)
        const responseType = data.type || 'message';
        const responseContent = data.response || '';
        const offerData = data.offer || null;
        const requiresAction = data.requiresAction || false;
        
        // Check if the response contains an offer using structured format
        if (responseType === 'offer' && offerData) {
          const structuredResponse = {
            type: responseType,
            content: responseContent,
            offer: offerData,
            requiresAction: requiresAction
          };
          const offer = extractOfferDetails(structuredResponse);
          if (offer) {
            // Create offer immediately in backend - use conversationId from response
            await createOfferAndShowLink(offer, currentConversationId);
            // End conversation for end users; developer mode keeps chat open for follow-up questions.
            if (!chatbot?.developerMode) {
              setConversationEnded(true);
            }
          }
        } else {
          // Only add AI response message if it's NOT an offer
          setMessages(prev => [...prev, { 
            role: 'assistant', 
            content: responseContent 
          }]);
        }
      } else {
        // Handle specific error messages from server
        const errorMessage = data.error || 'An error occurred';
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      // Show user-friendly error message
      const errorMessage = error.message.includes('price list') || error.message.includes('No price list')
        ? t('embed.noDocNoCompany')
        : t('embed.errorOccurred');
      
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: errorMessage
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!conversationEnded) {
        sendMessage();
      }
    }
  };

  const createOfferAndShowLink = async (offer, conversationIdToUse) => {
    try {
      // Create offer in the backend
      // Use conversationIdToUse parameter (from API response) instead of state variable
      // This ensures we have the correct conversationId even on first message
      const offerResponse = await fetch('/api/offers/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          conversationId: conversationIdToUse || conversationId,
          offerDetails: offer.offerDetails,
          customerInfo: {},
          services: offer.services,
          totalAmount: offer.totalAmount,
          chatbotId: chatbotId,
          unlistedItems: offer.unlistedItems || [],
          delivery: offer.delivery === true,
          setup: offer.setup === true,
          servicePersonnel: offer.servicePersonnel === true,
          otherService: offer.otherService === true,
          sharedSelections: offer.sharedSelections || []
        }),
      });

      if (!offerResponse.ok) {
        throw new Error('Failed to create offer');
      }

      const offerData = await offerResponse.json();
      const offerUrl = `${window.location.origin}/offer/${offerData.offerId}?token=${offerData.accessToken}`;
      
      // Add message with offer link to chat
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: t('embed.offerPrepared'),
        offerLink: offerUrl
      }]);
    } catch (error) {
      console.error('Error creating offer:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: t('embed.offerCreateError') 
      }]);
    }
  };

  const speechBubbleHint =
    chatbot?.speechBubbleHint || t('embed.speechBubbleHint');
  const speechBubbleHintNote =
    chatbot?.speechBubbleHintNote || t('embed.speechBubbleHintNote');

  if (!chatbot) {
    if (isIframeEmbed && !configFailed) {
      return null;
    }
    const pendingMessage = configFailed ? t('embed.loadError') : t('embed.loadingChatbot');
    const pendingClass = isIframeEmbed
      ? 'flex flex-col items-end justify-end w-full h-full'
      : 'fixed bottom-4 right-4 z-50 pb-[env(safe-area-inset-bottom)] pr-[env(safe-area-inset-right)]';
    return (
      <div className={pendingClass}>
        <div className="bg-white rounded-lg shadow-lg p-4 max-w-[240px]">
          <p className="text-gray-600 text-sm">{pendingMessage}</p>
        </div>
      </div>
    );
  }

  const primaryColor = chatbot?.primaryColor || '#3b82f6';
  const primaryColorHover = darkenHex(primaryColor, 0.15);
  const isHostMobile = hostViewport.width < SM_BREAKPOINT;

  const rootClass = isIframeEmbed
    ? isOpen && isHostMobile
      ? 'fixed inset-0 z-50 flex flex-col w-full h-full'
      : 'flex flex-col items-end justify-end z-50 w-full h-full'
    : `fixed z-50 flex flex-col ${
        isOpen
          ? 'inset-0 sm:inset-auto sm:bottom-4 sm:right-4 sm:items-end'
          : 'bottom-4 right-4 items-end pb-[env(safe-area-inset-bottom)] pr-[env(safe-area-inset-right)]'
      }`;

  const chatWindowClass = isIframeEmbed
    ? isHostMobile
      ? 'bg-white shadow-xl flex flex-col w-full h-full'
      : isMaximized
      ? 'bg-white shadow-xl flex flex-col rounded-lg w-[600px] h-[700px]'
      : isMinimized
      ? 'bg-white shadow-xl flex flex-col rounded-lg w-80 h-[400px]'
      : 'bg-white shadow-xl flex flex-col rounded-lg w-96 h-[500px]'
    : `bg-white shadow-xl flex flex-col transition-all duration-300 w-full sm:rounded-lg ${
        isMaximized
          ? 'h-full min-h-[100dvh] sm:w-[600px] sm:h-[700px] sm:min-h-0'
          : isMinimized
          ? 'h-[70vh] sm:w-80 sm:h-[400px]'
          : 'h-full min-h-[100dvh] sm:w-96 sm:h-[500px] sm:min-h-0'
      }`;

  return (
    <div ref={widgetRootRef} className={rootClass}>
      {/* Chat Toggle Button (when closed) */}
      {!isOpen && (
        <div ref={launcherRef} className="flex flex-col-reverse items-end gap-2 shrink-0">
          <button
            type="button"
            onClick={() => setIsOpen(true)}
            className="relative z-10 text-white rounded-full shadow-lg transition-colors w-16 h-16 flex items-center justify-center shrink-0"
            style={{ backgroundColor: primaryColor }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = primaryColorHover; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = primaryColor; }}
            aria-label={chatbot.name}
          >
            <MessageCircle size={30} />
          </button>
          {showSpeechBubble && (
            <div className="relative max-w-[240px] mb-1 px-3 py-2.5 pr-7 bg-white text-gray-700 text-sm leading-snug rounded-lg shadow-md shrink-0">
              <button
                type="button"
                onClick={() => setSpeechBubbleDismissed(true)}
                className="absolute top-1 right-1 text-gray-400 hover:text-gray-600 p-0.5 rounded"
                aria-label={t('common.close')}
              >
                <X size={14} />
              </button>
              <span>{speechBubbleHint} </span>
              <span className="block text-xs italic mt-1 text-gray-600 pr-1">{speechBubbleHintNote}</span>
              <div
                className="absolute -bottom-1.5 right-5 w-3 h-3 bg-white rotate-45 shadow-sm"
                aria-hidden="true"
              />
            </div>
          )}
        </div>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className={chatWindowClass}>
          {/* Header */}
          <div
            className="text-white p-4 pt-[max(1rem,env(safe-area-inset-top))] rounded-t-lg flex items-center justify-between sm:pt-4"
            style={{ backgroundColor: primaryColor }}
          >
            <div className="flex items-center space-x-3 min-w-0 flex-1">
              <div className="w-8 h-8 flex-shrink-0 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                <MessageCircle size={16} />
              </div>
              <div className="min-w-0">
                <h3 className="font-semibold text-sm truncate">{chatbot.name}</h3>
                <p className="text-xs opacity-90 truncate">{chatbot.companyName}</p>
              </div>
            </div>
            <div className="flex items-center space-x-1 sm:space-x-2 flex-shrink-0">
              <button
                onClick={() => {
                  setIsMinimized(!isMinimized);
                  if (isMinimized) setIsMaximized(false);
                }}
                className="hidden sm:flex text-white hover:text-gray-200 p-1 rounded hover:bg-white hover:bg-opacity-20 transition-colors min-w-[44px] min-h-[44px] items-center justify-center"
                title={isMinimized ? t('common.normalSize') : t('common.minimize')}
              >
                <Minus size={16} />
              </button>
              <button
                onClick={() => {
                  setIsMaximized(!isMaximized);
                  if (isMaximized) setIsMinimized(false);
                }}
                className="text-white hover:text-gray-200 p-1 rounded hover:bg-white hover:bg-opacity-20 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
                title={isMaximized ? t('common.normalSize') : t('common.maximize')}
              >
                {isMaximized ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="text-white hover:text-gray-200 p-1 rounded hover:bg-white hover:bg-opacity-20 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          <ChatContent
            messages={messages}
            inputMessage={inputMessage}
            onInputChange={setInputMessage}
            onSendMessage={sendMessage}
            onKeyPress={handleKeyPress}
            isLoading={isLoading}
            conversationEnded={conversationEnded}
            hasDocument={hasDocument}
            primaryColor={primaryColor}
            primaryColorHover={primaryColorHover}
            maxWidthClass={isMaximized ? 'sm:max-w-[480px]' : isMinimized ? 'sm:max-w-[240px]' : 'sm:max-w-[280px]'}
          />
        </div>
      )}
    </div>
  );
};

export default EmbeddedChatbot; 